import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import WizardShell, { WizardStep } from '../../../components/wizard/WizardShell';
import { useCategoryZh } from '../../../components/MaterialFieldInputs';
import {
    BundleIssue,
    getMaterialBundlePreflight,
    MaterialBundlePayload,
    MaterialBundlePreflight,
    submitMaterialBundle,
} from '../../../api/bundle';
import { getMaterial } from '../../../api/material';
import { CapType, listCapTypes } from '../../../api/captype';
import { ApiError } from '../../../api/request';
import { useCan, useMe } from '../../../store';
import { confirmDialog } from '../../../utils/dialog';
import { toast } from '../../../utils/toast';
import StepBasic from './StepBasic';
import StepPhys from './StepPhys';
import StepRelations from './StepRelations';
import StepParams from './StepParams';
import StepReview from './StepReview';
import {
    clearDraft,
    emptyPayload,
    hasContent,
    STEPS,
    StepKey,
    stepOf,
    toRequest,
    useBundleDraft,
} from './useBundleDraft';

// 材料创建向导 (2026-09-16, 设计 doc/relate-import/07).
//
// 「新建材料」的唯一入口 —— 原来的新建弹窗随本页上线下线。
// 页面本身不写库: 5 步填出来的是一份材料套件载荷, 提交后一个审批工单、一个事务落全部内容
// (材料 + SKU + 能力位 + 理化 + 贴士挂载 + 参数行), 任一步失败整体回滚, 零残留。
//
// ?from=<xcode> 进入 = 以某个已有材料为模板新建: 预填类别/属性/理化/能力位/贴士,
// 清空编码/名称/SKU/参数 (同材质换颜色换厚度是最高频的建材场景).

function MaterialCreateWizard() {
    const history = useHistory();
    const location = useLocation();
    const cloneFrom = new URLSearchParams(location.search).get('from') || '';
    const [cloneReady, setCloneReady] = useState(!cloneFrom);
    const [cloneInit, setCloneInit] = useState<MaterialBundlePayload | undefined>();

    // 克隆源要先拉回来才能初始化草稿 (useBundleDraft 的 initial 只在首次挂载生效)
    useEffect(() => {
        if (!cloneFrom) return;
        getMaterial(cloneFrom)
            .then((d) => {
                const phys: MaterialBundlePayload['physValues'] = {};
                (d.physGroups || []).forEach((g) => {
                    if (g.params && Object.keys(g.params).length) {
                        phys[g.groupKey] = g.params as Record<string, unknown>;
                    }
                });
                setCloneInit({
                    // 编码与名称留空: 克隆出来的是一个新材料, 必须重新命名与取号
                    material: {
                        type1: d.type1 || '',
                        type2: d.type2 || '',
                        craft1: d.craft1,
                        craft2: d.craft2,
                        surface1: d.surface1,
                        surface2: d.surface2,
                        color: d.color,
                        colorHex: d.colorHex,
                        target: d.target,
                        coatingMm: d.coatingMm,
                        axisYTransmissionRatioRollMaterial:
                            d.axisYTransmissionRatioRollMaterial ?? undefined,
                        gloss: d.gloss,
                        traits: d.traits,
                        flexibility: d.flexibility,
                        characteristics: d.characteristics || [],
                    },
                    capabilities: (d.capability || [])
                        .filter((c) => c.isEnabled)
                        .map((c) => c.capKey),
                    physValues: Object.keys(phys).length ? phys : undefined,
                });
                toast(`已以 ${cloneFrom} 为模板预填, 请填写新的名称`);
            })
            .catch(() => toast('模板材料读取失败, 从空白开始'))
            .finally(() => setCloneReady(true));
    }, [cloneFrom]);

    if (!cloneReady) {
        return <div className="card p-6 text-sm text-slate-400">正在读取模板材料…</div>;
    }
    return <WizardBody key={cloneFrom} initial={cloneInit} onLeave={() => history.push('/materials')} />;
}

/**
 * CheckGroup 右栏预检的一档 (错误 / 告警 / 提示): 点计数展开具体条目, 每条可点跳到出问题的那一步.
 * 侧栏窄, 消息按两行截断; 完整报告在第 ⑤ 步.
 */
function CheckGroup({
    tone,
    icon,
    label,
    issues,
    emptyLabel,
    emptyTone,
    onGoto,
}: {
    tone: string;
    icon: string;
    label: string;
    issues: BundleIssue[];
    emptyLabel?: string;
    emptyTone?: string;
    onGoto: (i: BundleIssue) => void;
}) {
    const [open, setOpen] = useState(false);
    if (issues.length === 0) {
        if (!emptyLabel) return null;
        return <div className={`text-[11px] ${emptyTone || tone}`}>{emptyLabel}</div>;
    }
    return (
        <div>
            <button
                type="button"
                className={`text-[11px] ${tone} hover:underline flex items-center gap-1`}
                onClick={() => setOpen(!open)}
            >
                <span>
                    {icon} {issues.length} 条{label}
                </span>
                <span className="opacity-60">{open ? '▾' : '▸'}</span>
            </button>
            {open && (
                <div className="mt-1 space-y-1">
                    {issues.map((i, k) => (
                        <button
                            key={`${i.code}-${i.path}-${k}`}
                            type="button"
                            className="block w-full text-left text-[11px] leading-snug text-slate-600 bg-slate-50 hover:bg-slate-100 rounded px-1.5 py-1"
                            title={`${i.msg}\n${i.path}`}
                            onClick={() => onGoto(i)}
                        >
                            {i.msg}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function WizardBody({
    initial,
    onLeave,
}: {
    initial?: MaterialBundlePayload;
    onLeave: () => void;
}) {
    const history = useHistory();
    const draft = useBundleDraft(initial);
    const { payload, patchMaterial, setPayload, step, goto, report, checking } = draft;
    const canPublish = useCan('material', 'publish');
    // 草稿按登录用户分开存 (共享工位上换人不该看见别人的半张表单)
    const { username } = useMe();
    const { cat1Zh, cat2Zh } = useCategoryZh();

    const [preflight, setPreflight] = useState<MaterialBundlePreflight | null>(null);
    const [capTypes, setCapTypes] = useState<CapType[]>([]);
    const [ack, setAck] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitErr, setSubmitErr] = useState('');
    const [done, setDone] = useState<{ taskCode?: string; created?: number } | null>(null);

    const { type1, type2 } = payload.material;

    // 类别定了就拉 preflight: 适用理化组 / 能力位 / 框架材料键粒度 全从这里来.
    // 就地新建的理化组不落库 (进套件 newDicts), 不用重取 preflight.
    useEffect(() => {
        if (!type1) {
            setPreflight(null);
            return;
        }
        let alive = true;
        getMaterialBundlePreflight(type1, type2 || '')
            .then((d) => alive && setPreflight(d))
            .catch(() => alive && setPreflight(null));
        return () => {
            alive = false;
        };
    }, [type1, type2]);

    useEffect(() => {
        listCapTypes()
            .then(setCapTypes)
            .catch(() => undefined);
    }, []);

    const onGotoIssue = useCallback(
        (issue: BundleIssue) => goto(stepOf(issue.path)),
        [goto],
    );

    const physFilled = Object.keys(payload.physValues || {}).length;
    // 理化组总数含本套件待新建的草稿组 (newDicts.physSchemas)
    const physTotal =
        (preflight?.physGroups.length || 0) + (payload.newDicts?.physSchemas?.length || 0);

    const steps: WizardStep[] = useMemo(
        () =>
            STEPS.map((s) => ({
                key: s.key,
                label: s.label,
                optional: s.optional,
                hasError: draft.issuesByStep[s.key].some((i) => i.severity === 'error'),
                badge:
                    s.key === 'phys'
                        ? physTotal
                            ? `${physFilled}/${physTotal} 组`
                            : ''
                        : s.key === 'relations'
                          ? [
                                payload.skus?.length ? `SKU ${payload.skus.length}` : '',
                                payload.capabilities?.length
                                    ? `工艺 ${payload.capabilities.length}`
                                    : '',
                                payload.tips?.tipCodes?.length
                                    ? `贴士 ${payload.tips.tipCodes.length}`
                                    : '',
                            ]
                                .filter(Boolean)
                                .join(' · ')
                          : s.key === 'params'
                            ? payload.params?.length
                                ? `${payload.params.length} 行`
                                : ''
                            : '',
            })),
        [draft.issuesByStep, payload, physFilled, physTotal],
    );

    const summary = useMemo(
        () => [
            {
                label: '编码',
                value: payload.material.xcode || <span className="text-slate-300">自动取号</span>,
            },
            {
                label: '材料',
                value:
                    payload.material.nameZh ||
                    payload.material.nameEn || <span className="text-slate-300">未命名</span>,
            },
            {
                label: '类别',
                value: type1 ? (
                    `${cat1Zh(type1)}${type2 ? ' › ' + cat2Zh(type1, type2) : ''}`
                ) : (
                    <span className="text-slate-300">未选</span>
                ),
            },
            { label: '理化', value: physTotal ? `${physFilled}/${physTotal} 组` : '—' },
            {
                label: 'SKU',
                value: payload.skus?.length
                    ? `新建 ${payload.skus.filter((s) => !s.linked).length} · 关联 ${
                          payload.skus.filter((s) => s.linked).length
                      }`
                    : '—',
            },
            { label: '工艺类型', value: payload.capabilities?.length || '—' },
            { label: '小贴士', value: payload.tips?.tipCodes?.length || '—' },
            { label: '参数', value: payload.params?.length ? `${payload.params.length} 行` : '—' },
        ],
        [payload, type1, type2, physFilled, physTotal, cat1Zh, cat2Zh],
    );

    // 预检面板: 计数可点开看具体是哪几条 —— 只显示"1 条告警"而看不到内容,
    // 用户要么忽略它, 要么得跑到第 ⑤ 步才知道发生了什么.
    const checkPanel = (
        <div className="border-t border-slate-100 pt-2">
            <div className="text-xs font-semibold text-slate-600 mb-1.5">预检</div>
            {!report ? (
                <div className="text-[11px] text-slate-400">尚未预检</div>
            ) : (
                <div className="space-y-1">
                    <CheckGroup
                        tone="text-rose-600"
                        icon="✖"
                        label="错误"
                        issues={report.errors}
                        emptyLabel="✔ 无错误"
                        emptyTone="text-emerald-600"
                        onGoto={onGotoIssue}
                    />
                    <CheckGroup
                        tone="text-amber-600"
                        icon="⚠"
                        label="告警"
                        issues={report.warnings}
                        onGoto={onGotoIssue}
                    />
                    <CheckGroup
                        tone="text-slate-400"
                        icon="ⓘ"
                        label="提示"
                        issues={report.infos}
                        onGoto={onGotoIssue}
                    />
                </div>
            )}
        </div>
    );

    const idx = STEPS.findIndex((s) => s.key === step);
    const isLast = step === 'review';

    const next = async () => {
        // 第一步的两个必填在前端先挡一下: 类别没选时 preflight 拉不到, 后面几步没法渲染
        if (step === 'basic') {
            if (!type1 || !type2) {
                toast('请先选择材料类别 (一级 / 二级)');
                return;
            }
            if (!payload.material.nameZh && !payload.material.nameEn) {
                toast('请填写材料名称');
                return;
            }
        }
        await draft.runCheck();
        goto(STEPS[Math.min(idx + 1, STEPS.length - 1)].key);
    };


    const submit = async () => {
        setSubmitting(true);
        setSubmitErr('');
        try {
            const { data, message } = await submitMaterialBundle(toRequest(payload), {
                ackWarnings: ack,
            });
            // 服务端会重跑一遍预检, 报告以它为准 (前置数据可能已变; 被拒时界面要能看到真实告警)
            if (data.report) draft.adoptReport(data.report);
            if (data.accepted === false) {
                setSubmitErr(message || '提交被拒绝, 请按报告修改后重试');
                return;
            }
            clearDraft(username);
            setDone({ taskCode: data.taskCode, created: data.created });
        } catch (e) {
            setSubmitErr(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setSubmitting(false);
        }
    };

    const leave = async () => {
        if (!done && hasContent(payload)) {
            const ok = await confirmDialog('离开向导? 已填内容会保留为草稿, 下次进入可恢复。');
            if (!ok) return;
        }
        onLeave();
    };

    // 门禁用服务端原始判定 (draft.rawReport): 展示用的 report 会把"改过的那几条"先撤掉,
    // 拿它当门禁等于自己给自己放行。
    const raw = draft.rawReport;
    // stale: 上次预检之后载荷改过 (如从「跳过」/步骤栏进来), 旧报告不能放行, 等自动重跑出结果
    const canSubmit =
        !!raw &&
        !draft.stale &&
        raw.ok &&
        (raw.warnings.length === 0 || ack) &&
        !submitting &&
        !checking;

    const nav = done ? (
        <>
            <button type="button" className="btn-ghost" onClick={() => window.location.reload()}>
                继续新建
            </button>
            <button type="button" className="btn-primary" onClick={() => history.push('/materials')}>
                回到材料库
            </button>
        </>
    ) : (
        <>
            <button
                type="button"
                className="btn-ghost"
                disabled={idx === 0}
                onClick={() => goto(STEPS[Math.max(idx - 1, 0)].key)}
            >
                上一步
            </button>
            {!isLast && STEPS[idx].optional && (
                <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => goto(STEPS[idx + 1].key)}
                >
                    跳过
                </button>
            )}
            {isLast ? (
                <button
                    type="button"
                    className="btn-primary"
                    disabled={!canSubmit}
                    onClick={submit}
                >
                    {submitting ? '提交中…' : '提交'}
                </button>
            ) : (
                <button type="button" className="btn-primary" disabled={checking} onClick={next}>
                    {checking ? '预检中…' : '下一步 →'}
                </button>
            )}
        </>
    );

    return (
        <WizardShell
            title="新建材料"
            backLabel="返回材料库"
            onBack={leave}
            steps={steps}
            current={step}
            onStep={(k) => goto(k as StepKey)}
            summary={summary}
            checkPanel={checkPanel}
            nav={nav}
        >
            {(submitErr || draft.error) && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    {submitErr || draft.error}
                </div>
            )}

            {draft.restoredAt > 0 && (
                <div className="warnbox text-xs flex items-center gap-2">
                    <span>
                        已恢复上次未完成的草稿 (
                        {new Date(draft.restoredAt).toLocaleString('zh-CN')})
                    </span>
                    <button
                        type="button"
                        className="ml-auto text-brand-600 hover:underline"
                        onClick={() => {
                            clearDraft(username);
                            setPayload(emptyPayload());
                            draft.dismissRestored();
                        }}
                    >
                        丢弃草稿, 从头开始
                    </button>
                    <button
                        type="button"
                        className="text-slate-500 hover:underline"
                        onClick={draft.dismissRestored}
                    >
                        知道了
                    </button>
                </div>
            )}

            {step === 'basic' && (
                <StepBasic
                    payload={payload}
                    patch={patchMaterial}
                    issueByPath={draft.issueByPath}
                    canPublish={canPublish}
                />
            )}
            {step === 'phys' && (
                <StepPhys
                    payload={payload}
                    setPayload={setPayload}
                    preflight={preflight}
                    issueByPath={draft.issueByPath}
                />
            )}
            {step === 'relations' && (
                <StepRelations
                    payload={payload}
                    setPayload={setPayload}
                    capTypes={capTypes}
                    issueByPath={draft.issueByPath}
                />
            )}
            {step === 'params' && (
                <StepParams
                    payload={payload}
                    setPayload={setPayload}
                    preflight={preflight}
                    issueByPath={draft.issueByPath}
                />
            )}
            {step === 'review' && (
                <StepReview
                    payload={payload}
                    patch={patchMaterial}
                    report={report}
                    checking={checking}
                    ack={ack}
                    setAck={setAck}
                    canPublish={canPublish}
                    onGoto={onGotoIssue}
                    done={done}
                />
            )}
        </WizardShell>
    );
}

export default MaterialCreateWizard;
